import React, { useCallback, useRef } from "react";
import { Box, Typography, Paper, TextField, InputAdornment, CircularProgress } from "@mui/material";
import { Search, MyLocation } from "@mui/icons-material";
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px"
};

const defaultCenter = {
  lat: 22.7196,
  lng: 75.8577
};

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false
};

const libraries = ["places"];

export default function LocationPicker({ latitude, longitude, address, onLocationChange }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries
  });

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const center = (latitude && longitude)
    ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
    : defaultCenter;

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        mapRef.current?.panTo({ lat, lng });
        mapRef.current?.setZoom(15);
        const comps = extractFromPlace(place);
        const merged = [comps.business_name, comps.apartment_building, comps.area, comps.city].filter(Boolean).join(", ");
        onLocationChange({
          latitude: lat.toString(),
          longitude: lng.toString(),
          address: merged,
          addressComponents: comps
        });
      }
    }
  };

  const extractFromPlace = (place) => {
    const findComponent = (types) => {
      for (const t of types) {
        const comp = place.address_components?.find((c) => c.types.includes(t));
        if (comp) return comp.long_name;
      }
      return "";
    };

    const premise = findComponent(["premise", "subpremise"]);
    const streetNo = findComponent(["street_number"]);
    const route = findComponent(["route"]);
    const buildingParts = [premise, streetNo, route].filter(Boolean);
    const apartment_building = buildingParts.length ? buildingParts.join(", ") : "";

    return {
      business_name: place.name || "",
      apartment_building,
      area: findComponent(["sublocality_level_1", "sublocality_level_2", "neighborhood", "sublocality"]),
      city: findComponent(["locality", "administrative_area_level_3", "administrative_area_level_2"])
    };
  };

  const extractFromGeocode = (result) => {
    const findComponent = (types) => {
      for (const t of types) {
        const comp = result.address_components?.find((c) => c.types.includes(t));
        if (comp) return comp.long_name;
      }
      return "";
    };

    const premise = findComponent(["premise", "subpremise"]);
    const streetNo = findComponent(["street_number"]);
    const route = findComponent(["route"]);
    const buildingParts = [premise, streetNo, route].filter(Boolean);
    const apartment_building = buildingParts.length ? buildingParts.join(", ") : "";

    return {
      business_name: "",
      apartment_building,
      area: findComponent(["sublocality_level_1", "sublocality_level_2", "neighborhood", "sublocality"]),
      city: findComponent(["locality", "administrative_area_level_3", "administrative_area_level_2"])
    };
  };

  const reverseGeocode = async (lat, lng) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const comps = extractFromGeocode(results[0]);
        const merged = [comps.business_name, comps.apartment_building, comps.area, comps.city].filter(Boolean).join(", ");
        onLocationChange({
          latitude: lat.toString(),
          longitude: lng.toString(),
          address: merged || results[0].formatted_address,
          addressComponents: comps
        });
      } else {
        onLocationChange({
          latitude: lat.toString(),
          longitude: lng.toString(),
          address: `${lat}, ${lng}`
        });
      }
    });
  };

  const onMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    reverseGeocode(lat, lng);
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          mapRef.current?.panTo({ lat, lng });
          mapRef.current?.setZoom(15);
          reverseGeocode(lat, lng);
        },
        () => {
          alert("Unable to retrieve your location.");
        }
      );
    }
  };

  if (loadError) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">Error loading Google Maps. Check your API key.</Typography>
      </Paper>
    );
  }

  if (!isLoaded) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading map...
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            onLoad={(ref) => { autocompleteRef.current = ref; }}
            onPlaceChanged={onPlaceChanged}
            restrictions={{ country: "in" }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search for a location..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Autocomplete>
        </Box>
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.5,
            py: 0.8,
            cursor: "pointer",
            "&:hover": { bgcolor: "action.hover" }
          }}
          onClick={handleGetCurrentLocation}
        >
          <MyLocation fontSize="small" color="primary" />
          <Typography variant="body2" color="primary" sx={{ whiteSpace: "nowrap" }}>
            Current Location
          </Typography>
        </Paper>
      </Box>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={13}
        options={mapOptions}
        onClick={onMapClick}
        onLoad={onLoad}
      >
        {latitude && longitude && (
          <Marker position={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }} />
        )}
      </GoogleMap>

      {address && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Selected: {address}
        </Typography>
      )}
    </Box>
  );
}
